import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Grid, Box,
    FormControl, InputLabel, Select, MenuItem, FormHelperText, FormControlLabel, Switch, Divider, Typography,
    Autocomplete, CircularProgress
} from '@mui/material';
import { Save as SaveIcon, Close as CloseIcon } from '@mui/icons-material';

import * as actions from '../redux.actions';
import * as selectors from '../redux.selectors';
import { CLIENT_TYPES } from '../common';

const emptyForm = {
    name: '', description: '', type: 'PERMANENT',
    industry: '', country: '', contactEmail: '', contactPhone: '',
    logoUrl: '', notes: '',
    devManagerEmail: null,
    projectManagerEmail: null,
    minHours: '',
    isActive: true
};

const ClientDialog = () => {
    const dispatch = useDispatch();
    const isOpen = useSelector(selectors.isClientDialogOpenSelector);
    const selected = useSelector(selectors.selectedClientSelector);
    const loading = useSelector(selectors.loadingSelector);
    const employeeOptions = useSelector(selectors.employeeOptionsSelector) || [];
    const employeeOptionsLoading = useSelector(selectors.employeeOptionsLoadingSelector);

    const [form, setForm] = useState(emptyForm);
    const [errors, setErrors] = useState({});

    const isEdit = !!selected;

    useEffect(() => {
        if (isOpen && selected) {
            setForm({
                name: selected.name || '',
                description: selected.description || '',
                type: selected.type || 'PERMANENT',
                industry: selected.industry || '',
                country: selected.country || '',
                contactEmail: selected.contactEmail || '',
                contactPhone: selected.contactPhone || '',
                logoUrl: selected.logoUrl || '',
                notes: selected.notes || '',
                devManagerEmail: selected.devManagerEmail || null,
                projectManagerEmail: selected.projectManagerEmail || null,
                minHours: selected.minHours || '',
                isActive: selected.isActive !== undefined ? selected.isActive : true
            });
        } else if (isOpen) {
            setForm(emptyForm);
        }
        setErrors({});
    }, [isOpen, selected]);

    useEffect(() => {
        if (isOpen) dispatch(actions.fetchEmployeeOptions());
    }, [dispatch, isOpen]);

    const findEmployeeOption = (email) =>
        (employeeOptions || []).find((e) => (e.user?.email || e.employeeId) === email) || null;

    const handleClose = () => { dispatch(actions.closeClientDialog()); setForm(emptyForm); setErrors({}); };
    const handleChange = (field) => (e) => {
        const val = field === 'isActive' ? e.target.checked : e.target.value;
        setForm((p) => ({ ...p, [field]: val }));
        if (errors[field]) setErrors((p) => ({ ...p, [field]: null }));
    };

    const validate = () => {
        const ne = {};
        if (!form.name.trim()) ne.name = 'Name is required';
        else if (form.name.trim().length > 255) ne.name = 'Name must not exceed 255 characters';
        if (!form.type) ne.type = 'Type is required';
        if (form.contactEmail && !/^\S+@\S+\.\S+$/.test(form.contactEmail)) ne.contactEmail = 'Invalid email';
        setErrors(ne);
        return Object.keys(ne).length === 0;
    };

    const handleSubmit = () => {
        if (!validate()) return;
        const payload = {
            name: form.name.trim(),
            description: form.description.trim() || undefined,
            type: form.type,
            industry: form.industry.trim() || undefined,
            country: form.country.trim() || undefined,
            contactEmail: form.contactEmail.trim() || undefined,
            contactPhone: form.contactPhone.trim() || undefined,
            logoUrl: form.logoUrl.trim() || undefined,
            notes: form.notes.trim() || undefined,
            devManagerEmail: form.devManagerEmail || null,
            projectManagerEmail: form.projectManagerEmail || null,
            minHours: form.minHours === '' ? undefined : Number(form.minHours),
            isActive: form.isActive
        };
        Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);
        if (isEdit) dispatch(actions.updateClient(selected.id, payload));
        else dispatch(actions.createClient(payload));
    };

    return (
        <Dialog open={isOpen} onClose={handleClose} maxWidth="md" fullWidth PaperProps={{ sx: { minHeight: '500px' } }}>
            <DialogTitle>{isEdit ? 'Edit Client' : 'Create New Client'}</DialogTitle>
            <DialogContent dividers sx={{ maxHeight: '70vh', overflowY: 'auto' }}>
                <Box sx={{ pt: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>Basic Information</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={8}>
                            <TextField label="Name" fullWidth required size="small"
                                value={form.name} onChange={handleChange('name')}
                                error={!!errors.name} helperText={errors.name}
                                inputProps={{ maxLength: 255 }} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth required size="small" error={!!errors.type}>
                                <InputLabel>Type</InputLabel>
                                <Select value={form.type} label="Type" onChange={handleChange('type')}>
                                    {CLIENT_TYPES.map((t) => (
                                        <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                                    ))}
                                </Select>
                                {errors.type && <FormHelperText>{errors.type}</FormHelperText>}
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField label="Description" fullWidth multiline rows={2} size="small"
                                value={form.description} onChange={handleChange('description')} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField label="Industry" fullWidth size="small"
                                value={form.industry} onChange={handleChange('industry')}
                                inputProps={{ maxLength: 128 }} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField label="Country" fullWidth size="small"
                                value={form.country} onChange={handleChange('country')}
                                inputProps={{ maxLength: 128 }} />
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 3 }} />
                    <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>Contact</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField label="Contact Email" fullWidth size="small" type="email"
                                value={form.contactEmail} onChange={handleChange('contactEmail')}
                                error={!!errors.contactEmail} helperText={errors.contactEmail} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField label="Contact Phone" fullWidth size="small"
                                value={form.contactPhone} onChange={handleChange('contactPhone')}
                                inputProps={{ maxLength: 64 }} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField label="Logo URL" fullWidth size="small"
                                value={form.logoUrl} onChange={handleChange('logoUrl')} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField label="Notes" fullWidth multiline rows={2} size="small"
                                value={form.notes} onChange={handleChange('notes')} />
                        </Grid>
                        <Grid item xs={12}>
                            <Divider sx={{ my: 1 }} />
                            <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>Managers</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Autocomplete
                                size="small"
                                fullWidth
                                options={employeeOptions}
                                getOptionLabel={(option) => {
                                    if (!option) return '';
                                    const name = [option.firstname, option.middlename, option.lastname].filter(Boolean).join(' ').trim();
                                    const email = option.user?.email || option.employeeId || '';
                                    return name ? `${name} (${email})` : email;
                                }}
                                isOptionEqualToValue={(opt, val) =>
                                    (opt.user?.email || opt.employeeId) === (val.user?.email || val.employeeId)
                                }
                                value={findEmployeeOption(form.devManagerEmail)}
                                onChange={(_, newValue) =>
                                    setForm((p) => ({
                                        ...p,
                                        devManagerEmail: newValue ? (newValue.user?.email || newValue.employeeId || null) : null
                                    }))
                                }
                                loading={employeeOptionsLoading}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Dev Manager"
                                        placeholder="Select dev manager"
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {employeeOptionsLoading ? <CircularProgress color="inherit" size={18} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            )
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Autocomplete
                                size="small"
                                fullWidth
                                options={employeeOptions}
                                getOptionLabel={(option) => {
                                    if (!option) return '';
                                    const name = [option.firstname, option.middlename, option.lastname].filter(Boolean).join(' ').trim();
                                    const email = option.user?.email || option.employeeId || '';
                                    return name ? `${name} (${email})` : email;
                                }}
                                isOptionEqualToValue={(opt, val) =>
                                    (opt.user?.email || opt.employeeId) === (val.user?.email || val.employeeId)
                                }
                                value={findEmployeeOption(form.projectManagerEmail)}
                                onChange={(_, newValue) =>
                                    setForm((p) => ({
                                        ...p,
                                        projectManagerEmail: newValue ? (newValue.user?.email || newValue.employeeId || null) : null
                                    }))
                                }
                                loading={employeeOptionsLoading}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Project Manager"
                                        placeholder="Select project manager"
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {employeeOptionsLoading ? <CircularProgress color="inherit" size={18} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            )
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField label="Min Hours" fullWidth size="small" type="number"
                                value={form.minHours} onChange={handleChange('minHours')}
                                inputProps={{ step: '0.01', min: '0' }} />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={<Switch checked={form.isActive} onChange={handleChange('isActive')} />}
                                label={form.isActive ? 'Active' : 'Inactive'}
                            />
                        </Grid>
                    </Grid>
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2, gap: 1 }}>
                <Button onClick={handleClose} startIcon={<CloseIcon />} disabled={loading}>Cancel</Button>
                <Button onClick={handleSubmit} variant="contained" startIcon={<SaveIcon />} disabled={loading}>
                    {isEdit ? 'Update' : 'Create'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ClientDialog;
